/**
 * Per-arm totals, then each check by arm (where the arms differ, and on what), with code
 * checks and judge checks apart, then the run stats: errors, time, tokens and cost.
 *
 * The summary has no check kinds, so they come from the trial results. Pass rates, model time
 * and warm-cache cost are shaded from the best arm (green) to the worst (red).
 */
import type { ReactNode } from 'react';
import type { ArmSummary, CheckResult, EvalModel, EvalRunItem, StoredTrialResult } from '../../types/evalTypes';
import { armCacheWriteCost, armCost, armWarmCostPer1k, CACHE_WRITE_NOTE, COST_NOTE, formatUsd, WARM_CACHE_NOTE } from '../../utils/evalCost';
import { formatDuration, formatPassRate, modelLabel } from '../../utils/evalFormat';
import { rate, shades, TOLERANCE } from '../../utils/evalShades';
import { ArmLabel } from '../ArmLabel';

interface ArmSummaryTableProps {
  run: EvalRunItem;
  /** For the check kinds and the per-kind totals. */
  results: StoredTrialResult[];
  /** For the prices and the judge's label. */
  models: EvalModel[];
  /** For the production badge. */
  productionModel: string | undefined;
}

type CheckKind = CheckResult['kind'];

const tokens = (input: number, output: number) => `${input.toLocaleString()} in / ${output.toLocaleString()} out`;

/** Check IDs in the order they first appear, which is the order the suite runs them. */
const checkIds = (arms: ArmSummary[]): string[] => [...new Set(arms.flatMap((arm) => Object.keys(arm.checks)))];

/** A labelled section break across the whole table, styled like the header. */
const SectionRow = ({ label, hint, columns }: { label: string; hint?: string; columns: number }) => (
  <tr className="border-t-2 border-gray-200 bg-gray-50">
    <td colSpan={columns} className="px-4 py-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      {hint && <span className="ml-2 text-xs text-gray-400">{hint}</span>}
    </td>
  </tr>
);

/**
 * Per arm: of the trials that got checks of this kind, how many passed all of them. A trial
 * that failed a base check (refusal, cut off, empty) gets no judge checks, so it is not in
 * the judge total.
 */
function kindTotals(results: StoredTrialResult[], armId: string, kind: CheckKind): { passed: number; graded: number } {
  const graded = results.filter((result) => result.armId === armId && result.status !== 'skipped' && result.checks.some((check) => check.kind === kind));
  const passed = graded.filter((result) => result.checks.filter((check) => check.kind === kind).every((check) => check.verdict === 'pass'));
  return { passed: passed.length, graded: graded.length };
}

const StatRow = ({ label, title, indent, children }: { label: string; title?: string; indent?: boolean; children: ReactNode }) => (
  <tr className="border-t border-gray-100" title={title}>
    <td className={`px-4 py-1.5 text-gray-700 ${indent ? 'pl-8' : ''}`}>{label}</td>
    {children}
  </tr>
);

export const ArmSummaryTable = ({ run, results, models, productionModel }: ArmSummaryTableProps) => {
  const arms = run.summary.arms;
  const columns = arms.length + 1;
  const infoById = new Map(run.arms.map((arm) => [arm.id, arm]));

  const kinds = new Map<string, CheckKind>();
  results.forEach((result) => result.checks.forEach((check) => kinds.set(check.id, check.kind)));
  // A check no result has (a run with no results yet) is shown with the code checks.
  const checksOf = (kind: CheckKind) => checkIds(arms).filter((id) => (kinds.get(id) ?? 'code') === kind);

  const judgeVersions = run.judgePrompts.map((judge) => `${judge.judgeName} prompt v${judge.number}`).join(', ');
  const groups: { kind: CheckKind; label: string; hint: string; totalLabel: string }[] = [
    { kind: 'code', label: 'Code checks', hint: 'Deterministic rules in code: same output, same verdict.', totalLabel: 'All code checks passed' },
    {
      kind: 'judge',
      label: 'Judge checks',
      hint: `${modelLabel(models, run.judgeModel)}${judgeVersions ? `, ${judgeVersions}` : ''}. Only for trials that passed the base checks.`,
      totalLabel: 'All judge checks passed',
    },
  ];

  const graded = (totals: { pass: number; fail: number; unknown: number }) => totals.pass + totals.fail + totals.unknown;

  const checkRow = (checkId: string) => {
    const rowShades = shades(
      arms.map((arm) => {
        const totals = arm.checks[checkId];
        return totals ? rate(totals.pass, graded(totals)) : null;
      }),
      'higher',
    );
    return (
      <tr key={checkId} className="border-t border-gray-100">
        <td className="px-4 py-1.5 pl-8 font-mono text-gray-700">{checkId}</td>
        {arms.map((arm, index) => {
          const totals = arm.checks[checkId];
          if (!totals) {
            return (
              <td key={arm.armId} className="px-4 py-1.5 text-gray-300">
                –
              </td>
            );
          }
          const tone = rowShades[index] ?? (totals.fail > 0 ? 'text-red-700' : totals.unknown > 0 ? 'text-amber-700' : 'text-gray-700');
          return (
            <td key={arm.armId} className={`px-4 py-1.5 ${tone}`}>
              {formatPassRate(totals.pass, graded(totals))}
              {totals.unknown > 0 && <span className="ml-1">({totals.unknown} unknown)</span>}
            </td>
          );
        })}
      </tr>
    );
  };

  const passShades = shades(
    arms.map((arm) => rate(arm.passed, arm.trials)),
    'higher',
  );
  const timeShades = shades(
    arms.map((arm) => arm.medianDurationMs),
    'lower',
    { format: formatDuration, tolerance: TOLERANCE },
  );
  const warmCosts = arms.map((arm) => armWarmCostPer1k(models, run, arm));
  const warmCostShades = shades(warmCosts, 'lower', { format: formatUsd, tolerance: TOLERANCE });

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="font-medium px-4 py-2">Check</th>
            {arms.map((arm) => {
              const info = infoById.get(arm.armId);
              return (
                <th key={arm.armId} className="font-normal px-4 py-2">
                  {info ? <ArmLabel arm={info} models={models} productionModel={productionModel} compact /> : arm.armId}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-gray-100 font-medium">
            <td className="px-4 py-2">All checks passed</td>
            {arms.map((arm, index) => (
              <td key={arm.armId} className={`px-4 py-2 ${passShades[index] ?? ''}`}>
                {formatPassRate(arm.passed, arm.trials)}
              </td>
            ))}
          </tr>
          {groups.map((group) => {
            const ids = checksOf(group.kind);
            if (ids.length === 0) return null;
            const totals = arms.map((arm) => kindTotals(results, arm.armId, group.kind));
            const totalShades = shades(
              totals.map((total) => rate(total.passed, total.graded)),
              'higher',
            );
            return [
              <SectionRow key={`${group.kind}-section`} label={group.label} hint={group.hint} columns={columns} />,
              <tr key={`${group.kind}-total`} className="border-t border-gray-100 font-medium">
                <td className="px-4 py-1.5 text-gray-800">{group.totalLabel}</td>
                {arms.map((arm, index) => (
                  <td key={arm.armId} className={`px-4 py-1.5 ${totalShades[index] ?? 'text-gray-800'}`}>
                    {formatPassRate(totals[index].passed, totals[index].graded)}
                  </td>
                ))}
              </tr>,
              ...ids.map(checkRow),
            ];
          })}

          <SectionRow label="Run stats" columns={columns} />
          <StatRow label="Errors / refusals / skipped">
            {arms.map((arm) => (
              <td key={arm.armId} className={`px-4 py-1.5 ${arm.errors + arm.refusals > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                {arm.errors} / {arm.refusals} / {arm.skipped}
              </td>
            ))}
          </StatRow>
          <StatRow label="Median model time" title="Time of the call to the model under test only. Judge calls are not included.">
            {arms.map((arm, index) => (
              <td key={arm.armId} className={`px-4 py-1.5 ${timeShades[index] ?? 'text-gray-700'}`}>
                {arm.medianDurationMs === null ? '–' : formatDuration(arm.medianDurationMs)}
              </td>
            ))}
          </StatRow>
          <StatRow label="Model tokens">
            {arms.map((arm) => (
              <td key={arm.armId} className="px-4 py-1.5 text-gray-700">
                {tokens(arm.targetTokens.input + arm.targetTokens.cacheRead + arm.targetTokens.cacheCreation, arm.targetTokens.output)}
              </td>
            ))}
          </StatRow>
          <StatRow label="Est. model cost" title={COST_NOTE}>
            {arms.map((arm) => (
              <td key={arm.armId} className="px-4 py-1.5 text-gray-700">
                {formatUsd(armCost(models, run, arm).model)}
              </td>
            ))}
          </StatRow>
          <StatRow label="of which cache writes" title={CACHE_WRITE_NOTE} indent>
            {arms.map((arm) => {
              const { cacheRead, cacheCreation } = arm.targetTokens;
              const cached = cacheRead + cacheCreation;
              return (
                <td key={arm.armId} className="px-4 py-1.5 text-gray-700">
                  {formatUsd(armCacheWriteCost(models, run, arm))}
                  {cached > 0 && (
                    <span className="ml-2 text-xs text-gray-400">
                      {cacheCreation.toLocaleString()} tokens, {Math.round((cacheRead / cached) * 100)}% hits
                    </span>
                  )}
                </td>
              );
            })}
          </StatRow>
          <StatRow label="Model cost / 1k replies, warm cache" title={WARM_CACHE_NOTE}>
            {arms.map((arm, index) => (
              <td key={arm.armId} className={`px-4 py-1.5 font-medium ${warmCostShades[index] ?? 'text-gray-800'}`}>
                {formatUsd(warmCosts[index])}
              </td>
            ))}
          </StatRow>

          <SectionRow label="Judge" hint={`${modelLabel(models, run.judgeModel)}. What grading the run cost, not part of the model cost.`} columns={columns} />
          <StatRow label="Judge tokens">
            {arms.map((arm) => (
              <td key={arm.armId} className="px-4 py-1.5 text-gray-700">
                {tokens(arm.judgeTokens.input + arm.judgeTokens.cacheRead + arm.judgeTokens.cacheCreation, arm.judgeTokens.output)}
              </td>
            ))}
          </StatRow>
          <StatRow label="Est. judge cost" title={COST_NOTE}>
            {arms.map((arm) => (
              <td key={arm.armId} className="px-4 py-1.5 text-gray-700">
                {formatUsd(armCost(models, run, arm).judge)}
              </td>
            ))}
          </StatRow>
        </tbody>
      </table>
    </div>
  );
};
